import type {
  NativeBooking,
  NativeEditProposal,
  NativeResultPdf,
  NativeResultPdfAvailability,
  NativeSuggestion,
} from './native-booking-api';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string') {
    throw new Error(`Invalid native booking payload: ${field} must be a string`);
  }
  return value;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function requireNumber(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Invalid native booking payload: ${field} must be a number`);
  }
  return value;
}

const RESULT_PDF_AVAILABILITIES = new Set<NativeResultPdfAvailability>([
  'AVAILABLE',
  'PENDING',
  'MISSING',
  'NOT_READY',
]);

function parseResultPdf(value: unknown): NativeResultPdf | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const availability = value.availability;
  if (typeof availability !== 'string' || !RESULT_PDF_AVAILABILITIES.has(availability as NativeResultPdfAvailability)) {
    return undefined;
  }
  return {
    availability: availability as NativeResultPdfAvailability,
    inspection_case_id: optionalString(value.inspection_case_id),
    case_status: optionalString(value.case_status),
    message:
      typeof value.message === 'string' && value.message.trim() !== ''
        ? value.message
        : 'Осмотр не завершён',
  };
}

export function parseNativeBooking(body: unknown): NativeBooking {
  if (!isRecord(body)) {
    throw new Error('Invalid native booking payload: expected object');
  }
  const draft = isRecord(body.draft) ? body.draft : {};
  const resultPdf = parseResultPdf(body.result_pdf);
  return {
    id: requireString(body.id, 'id'),
    public_number: requireString(body.public_number, 'public_number'),
    status: requireString(body.status, 'status'),
    inspection_site_id: requireString(body.inspection_site_id, 'inspection_site_id'),
    equipment_type_id: requireString(body.equipment_type_id, 'equipment_type_id'),
    slot_start_at: requireString(body.slot_start_at, 'slot_start_at'),
    slot_end_at: requireString(body.slot_end_at, 'slot_end_at'),
    hold_generation: requireNumber(body.hold_generation, 'hold_generation'),
    hold_expires_at: optionalString(body.hold_expires_at),
    current_inspection_case_id: optionalString(body.current_inspection_case_id),
    draft,
    version: requireString(body.version, 'version'),
    booking_source: 'NATIVE',
    editable: body.editable === true,
    case_status: optionalString(body.case_status),
    ...(resultPdf !== undefined ? { result_pdf: resultPdf } : {}),
  };
}

export function parseNativeBookingList(body: unknown): readonly NativeBooking[] {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new Error('Invalid native booking list: expected { items: NativeBooking[] }');
  }
  return body.items.map((row) => parseNativeBooking(row));
}

export function parseNativeSuggestion(body: unknown): NativeSuggestion {
  if (!isRecord(body)) {
    throw new Error('Invalid suggestion payload');
  }
  return {
    id: requireString(body.id, 'id'),
    semantic_key: requireString(body.semantic_key, 'semantic_key'),
    value: requireString(body.value, 'value'),
  };
}

export function parseNativeSuggestionList(body: unknown): readonly NativeSuggestion[] {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new Error('Invalid suggestion list: expected { items: [...] }');
  }
  return body.items.map((row) => parseNativeSuggestion(row));
}

export function parseNativeEditProposal(body: unknown): NativeEditProposal {
  if (!isRecord(body)) {
    throw new Error('Invalid edit proposal payload');
  }
  const draft = isRecord(body.draft) ? body.draft : {};
  return {
    id: requireString(body.id, 'id'),
    booking_id: requireString(body.booking_id, 'booking_id'),
    status: requireString(body.status, 'status'),
    base_booking_version: requireString(body.base_booking_version, 'base_booking_version'),
    proposal_version: requireString(body.proposal_version, 'proposal_version'),
    inspection_site_id: requireString(body.inspection_site_id, 'inspection_site_id'),
    equipment_type_id: requireString(body.equipment_type_id, 'equipment_type_id'),
    slot_start_at: requireString(body.slot_start_at, 'slot_start_at'),
    slot_end_at: requireString(body.slot_end_at, 'slot_end_at'),
    hold_generation:
      body.hold_generation === null || body.hold_generation === undefined
        ? null
        : requireNumber(body.hold_generation, 'hold_generation'),
    hold_expires_at: optionalString(body.hold_expires_at),
    expires_at: optionalString(body.expires_at),
    draft,
  };
}

/** Stable document draft key matching API `documentIdentityKey(typeId, subject)`. */
export function documentIdentityKey(documentTypeId: string, subjectBinding: string): string {
  return `${documentTypeId}::${subjectBinding}`;
}
